// What People's list and a member's page share (01-tenants-and-roles.md §
// mapping rows; 05-invitations.md § the console and the terminal;
// 01-the-console.md § Settings: the Sign-in tab, people under one
// provider): which provider the workspace signs in through and what each row
// signs in with, and the acts on a person's row — *Send again* (or *Send
// invite*), *Disconnect*, *Remove* — each asked first where the design asks.

import { useState } from "react";

import { tenantPath } from "../../address/address.ts";
import { PLATFORM_NAME, providerName } from "../../platform/names.ts";
import type { TenantProfile } from "../../platform/profile.ts";
import { useLoad } from "../../ui/components.tsx";
import type { MenuEntry } from "../../ui/table.tsx";
import {
  ProviderID,
  Subjects,
  type KickReply,
  type KickRequest,
  type MemberInviteRequest,
  type ProviderReply,
  type Row,
  type RowDeleteRequest,
  type RowReply,
} from "../../wire/auth.ts";
import { SERVICE } from "../../wire/identity.ts";
import type { Surface } from "../surface.tsx";
import { inviteLine } from "./invite.ts";
import { personName } from "./rows.ts";

/** rowKey names a row: its provider, claim and value. */
export function rowKey(r: Pick<Row, "provider" | "claim" | "value">): string {
  return `${r.provider}/${r.claim}/${r.value}`;
}

/**
 * memberPath is a member's page, `people/<provider>/<claim>/<value>`: the
 * three parts that key their row, each encoded as one segment, so a value
 * with a slash or a space — an email, a subject — stays one segment.
 */
export function memberPath(slug: string, r: Pick<Row, "provider" | "claim" | "value">): string {
  return tenantPath(slug, "people", r.provider, r.claim, r.value);
}

/** Signing is which provider the workspace signs in through, and the names the page uses for each. */
export interface Signing {
  inUse: string;
  ownName: string;
  signsInWith: (r: Row) => string;
}

/**
 * useSigning reads the provider in use (decision 0055). The read is refused
 * without the plan's entitlement; such a workspace has no own provider, so
 * the profile's provider, where it names one, is the managed one.
 */
export function useSigning(s: Surface, tenant: TenantProfile): Signing {
  const [provider] = useLoad("provider", () =>
    s.bus.call<ProviderReply>(SERVICE, Subjects.providerGet(), undefined),
  );
  const inUse =
    provider.state === "ready"
      ? (provider.value.in_use ?? ProviderID.platform)
      : tenant.provider !== undefined
        ? ProviderID.managed
        : ProviderID.platform;
  const ownName = tenant.provider === undefined ? `${s.name}'s own sign-in` : providerName(tenant.provider);
  return {
    inUse,
    ownName,
    signsInWith: (r: Row) => (r.provider === ProviderID.own ? ownName : PLATFORM_NAME),
  };
}

/** PeopleActs is the acts on a person's row, and what the last one said. */
export interface PeopleActs {
  err: unknown;
  note: string | undefined;
  setNote: (n: string | undefined) => void;
  disconnect: (r: Row) => void;
  /** The row menu's entries for a person, Edit offered through `onEdit` where the row may be edited. */
  memberMenu: (r: Row, onEdit?: (r: Row) => void) => MenuEntry[];
  remove: (r: Row) => void;
}

export function usePeopleActs(s: Surface, reload: () => void, onDisconnect?: () => void): PeopleActs {
  const [err, setErr] = useState<unknown>(undefined);
  const [note, setNote] = useState<string | undefined>(undefined);
  const remove = (r: Row) => {
    setErr(undefined);
    const body: RowDeleteRequest = { provider: r.provider, claim: r.claim, value: r.value };
    s.bus.call<RowReply>(SERVICE, Subjects.rowsDelete(), body).then(
      () => {
        reload();
      },
      (e: unknown) => {
        setErr(e);
      },
    );
  };
  const sendAgain = (r: Row) => {
    setErr(undefined);
    const body: MemberInviteRequest = { provider: r.provider, claim: r.claim, value: r.value };
    s.bus.call<RowReply>(SERVICE, Subjects.membersInvite(), body).then(
      (reply) => {
        setNote(inviteLine(reply.row)?.text ?? `${personName(r)}'s invite is sent again.`);
        reload();
      },
      (e: unknown) => {
        setErr(e);
      },
    );
  };
  const disconnect = (r: Row) => {
    setErr(undefined);
    onDisconnect?.();
    s.bus.call<KickReply>(SERVICE, Subjects.kick(), { subject: r.value } satisfies KickRequest).then(
      (reply) => {
        setNote(
          reply.kicked === 0
            ? `${personName(r)} was not connected.`
            : `${personName(r)} is disconnected (${reply.kicked} ${reply.kicked === 1 ? "connection" : "connections"}).`,
        );
      },
      (e: unknown) => {
        setErr(e);
      },
    );
  };
  const memberMenu = (r: Row, onEdit?: (r: Row) => void): MenuEntry[] => {
    const entries: MenuEntry[] = [];
    const invite = inviteLine(r);
    if (invite?.sendAgain === true && s.offered("people:members:add")) {
      entries.push({
        label: invite.first === true ? "Send invite" : "Send again",
        onSelect: () => {
          sendAgain(r);
        },
      });
    }
    if (r.required !== true && onEdit !== undefined) {
      entries.push({
        label: "Edit",
        onSelect: () => {
          onEdit(r);
        },
      });
    }
    if (r.pending !== true && s.offered("people:kick")) {
      entries.push({
        label: "Disconnect",
        confirm: {
          title: `Disconnect ${personName(r)}?`,
          consequence: "Their connections close now. They can sign in again as the rules allow.",
        },
        onSelect: () => {
          disconnect(r);
        },
      });
    }
    entries.push({
      label: "Remove",
      danger: true,
      confirm: {
        title: `Remove ${personName(r)}?`,
        consequence: "They are not allowed in at their next sign-in.",
      },
      onSelect: () => {
        remove(r);
      },
    });
    return entries;
  };
  return { err, note, setNote, disconnect, memberMenu, remove };
}
